// Which notification a staff save on a feedback request sends (PATCH /api/admin/feedback/:id). The API acts on
// it and the console's Notify checkbox shows it, so both always agree. At most one notification per save:
// a resolution, a new estimate, or the other firm-visible changes as one update. A reason edit alone, or an
// estimate on a closed request, sends nothing.
import { mentionsToPlain } from './mentions.js';
export const FEEDBACK_OPEN_STATUSES = ['received', 'planned', 'in_progress'];
/** The Done note or Won't do reason the requester sees (feedback_requests.status_reason, migration 007). */
export const FEEDBACK_STATUS_REASON_MAX = 1000;
export function feedbackPatchNotice(save) {
    const statusChanged = save.to !== save.from;
    const open = FEEDBACK_OPEN_STATUSES.includes(save.to);
    if (statusChanged && (save.to === 'done' || save.to === 'wont_do'))
        return { kind: 'resolved', status: save.to };
    if (save.estimateChanged && save.estimate && open)
        return { kind: 'estimate', date: save.estimate };
    const parts = [];
    if (statusChanged && open)
        parts.push('status');
    if (save.estimateChanged && !save.estimate)
        parts.push('estimate_removed');
    if (save.title)
        parts.push('title');
    if (save.area)
        parts.push('area');
    return parts.length ? { kind: 'update', parts } : null;
}
/** The console's Notify default: on for a resolution or a new estimate, off for a minor update. */
export const notifiesByDefault = (notice) => notice.kind !== 'update';
// The text of a feedback notification in the firm's inbox (the portal shows the title, then the body). The API
// writes it to the outbox and the console's "Preview message to client" shows it, from these same functions.
/**
 * A message body as plain text. Staff format comments with a small Markdown subset (bold, italic, lists, links:
 * the console's FormatBar); the bell shows text only, so the marks come off and a link keeps its words.
 */
export function feedbackPlainText(text) {
    return mentionsToPlain(text)
        .replace(/\[([^\]\n]+)\]\((?:https?:|mailto:)[^)\s]*\)/g, '$1')
        .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '$1')
        .replace(/(^|[^\w*\\])\*(?=[^\s*])([^*\n]*?[^\s*\\])\*(?![\w*])/g, '$1$2')
        .replace(/\\([*_[\]()\\])/g, '$1');
}
/** Plain text (marks removed), whitespace collapsed and cut to `max` characters with an ellipsis. */
export function feedbackExcerpt(text, max = 160) {
    const clean = feedbackPlainText(text).replace(/\s+/g, ' ').trim();
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}
/** `reference` is the formatted one: FB-0006. */
export const feedbackInboxTitle = (reference, title) => `${reference} · ${title}`;
/** Done carries the note when there is one; Won't do and Duplicate carry their reason. */
export function feedbackResolvedBody(status, title, reason) {
    if (status === 'done')
        return reason?.trim() ? `Marked Done: ${feedbackExcerpt(reason)}` : `"${feedbackExcerpt(title, 120)}" was marked Done.`;
    return reason ? feedbackExcerpt(reason) : null;
}
/** `date` is YYYY-MM-DD. */
export function feedbackEstimateBody(date) {
    const formatted = new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    return `Estimated completion ${formatted}.`;
}
const UPDATE_TEXT = {
    status: (s) => `Status: ${s.replaceAll('_', ' ')}`, estimate_removed: () => 'Estimate removed', title: () => 'Title updated', area: () => 'Area updated',
};
/** `status` is the request's status after the save. */
export const feedbackUpdateBody = (parts, status) => parts.map((p) => UPDATE_TEXT[p](status)).join(' · ');
// The auto-fix runner's progress on a request (feedback_autofix_runs.state, migration 019). The runner, a Claude
// Code scheduled task on a staff Mac, reports each step through the agent API; the console's Auto-fix card shows
// it. `waiting`: the review artifact is published and waits for a staff decision. Firms never see any of it.
export const AUTOFIX_STATES = ['intake', 'root_cause', 'fix', 'review', 'waiting', 'merged', 'rejected', 'failed'];
/** The longest step detail the runner can send ("Draft PR #31 · 2 files · tests pass"). */
export const AUTOFIX_DETAIL_MAX = 200;
/** Labels staff see for a request's area and urgency (the console and the new-feedback email share them). */
export const FEEDBACK_AREA_LABEL = {
    clients: 'Clients', engagements: 'Engagements', jobs_tasks: 'Jobs & tasks', invoices_billing: 'Invoices & billing',
    documents: 'Documents', esignature: 'E-signature', client_portal: 'Client portal', communications: 'Communications',
    tax_advisory: 'Tax advisory', reports_insights: 'Reports & insights', settings: 'Settings', other: 'Something else',
};
export const FEEDBACK_URGENCY_LABEL = { blocking: 'Blocking them', slowing: 'Slowing them down', fyi: 'Just FYI' };
/** Settings → New feedback emails: only addresses at this domain can be on the list (the recipient guard checks it too). */
export const FEEDBACK_EMAIL_DOMAIN = 'vertekal.com';
export const FEEDBACK_EMAIL_RECIPIENTS_MAX = 20;
export const isFeedbackEmailAddress = (email) => /^[^\s@,;<>]+@vertekal\.com$/.test(email.trim().toLowerCase());
//# sourceMappingURL=feedback.js.map